import { HistoryItem, Order, OrderComment, OrdersResponse, Quote } from '@/interfaces/order.interface';
import OrderService from '@/services/api/order.service';
import { OrderDocument } from '@/interfaces/document.interface';
import { VuetifyPaginationInterface } from '@/interfaces/vuetify-pagination.interface';

export const orderActions = {
  async fetchOrders({commit, state}: any) {
    commit('LOADING_START', null, {root: true});
    try {
      const filters = state.orderFilters;
      const pagination = state.pagination;
      const response: OrdersResponse = await OrderService.getAll(pagination, filters);
      commit('LOADING_FINISH', null, {root: true});
      commit('setOrders', {
        orders: response.orders,
        page: response.page,
        itemsPerPage: response.itemsPerPage,
        totalItems: response.totalItems
      });
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  async fetchOneOrder({commit, state}: any, {orderUuid}: { orderUuid: string }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response: OrdersResponse = await OrderService.getOne(orderUuid);
      commit('LOADING_FINISH', null, {root: true});
      if (state.orders.find((o: Order) => o.uuid === orderUuid)) {
        commit('update', {order: response});
      } else {
        commit('addOrder', response);
      }
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  setPagination({commit}: any, {pagination}: {pagination: VuetifyPaginationInterface}) {
    commit('setPagination', {pagination});
  },
  async create({commit, dispatch}: any, {order}: { order: Order }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await OrderService.create(order);
      commit('create', {order: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Creating order failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async update({commit, dispatch}: any, {order}: { order: Order }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await OrderService.update(order);
      commit('update', {order: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Updating order failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async deleteWithConfirmation({commit}: any, {order}: { order: Order }) {
    commit('SHOW_CONFIRMATION', {
      message: `Delete order ${order.orderNumber}?`,
      action: 'orders/delete',
      payload: {order}
    }, {root: true});
  },
  async delete({commit, dispatch}: any, {order}: { order: Order }) {
    commit('LOADING_START', null, {root: true});
    try {
      if (!order.uuid) return dispatch('SET_ALERT_MESSAGE', `Can't delete an order without a uuid`, {root: true});
      await OrderService.delete(order.uuid);
      commit('delete', {order});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Deleting order failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  setOrdersFilter({commit}: any, filters: any) {
    commit('setOrdersFilter', filters);
  },
  async fetchStatuses({commit, dispatch}: any) {
    try {
      const statuses = await OrderService.fetchStatuses();
      commit('setStatuses', {statuses});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Fetching order statuses failed', {root: true});
    }
  },
  async fetchPriorities({commit, dispatch}: any) {
    try {
      const priorities = await OrderService.fetchPriorities();
      commit('setPriorities', {priorities});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Fetching order priorities failed', {root: true});
    }
  },
  async fetchTransportModes({commit, dispatch}: any) {
    try {
      const transportModes = await OrderService.fetchTransportModes();
      commit('setTransportModes', {transportModes});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Fetching order transport modes failed', {root: true});
    }
  },
  async fetchHistory({commit}: any, {orderUuid}: { orderUuid: string }) {
    commit('LOADING_START', null, {root: true});
    try {
      const historyItems: HistoryItem[] = await OrderService.fetchHistory(orderUuid);
      commit('LOADING_FINISH', null, {root: true});
      commit('setHistoryItems', {orderUuid, historyItems});
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  async fetchQuotes({commit}: any, {orderUuid}: { orderUuid: string }) {
    commit('LOADING_START', null, {root: true});
    try {
      const quotes: Quote[] = await OrderService.fetchQuotes(orderUuid);
      commit('LOADING_FINISH', null, {root: true});
      commit('setQuotes', {orderUuid, quotes});
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  async createQuote({commit, dispatch}: any, {orderUuid, quote}: { orderUuid: string, quote: Quote }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await OrderService.createQuote(orderUuid, quote);
      commit('createQuote', {quote: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Creating quote failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async updateQuote({commit, dispatch}: any, {quote}: { quote: Quote }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await OrderService.updateQuote(quote);
      commit('updateQuote', {quote: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Updating quote failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async selectQuote({commit, dispatch}: any, {quote}: { quote: Quote }) {
    commit('LOADING_START', null, {root: true});
    try {
      const updatedQuote = {
        ...quote,
        ...{selected: true}
      };
      const response = await OrderService.updateQuote(updatedQuote);
      commit('updateQuote', {quote: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Updating quote failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async unSelectQuote({commit, dispatch}: any, {quote}: { quote: Quote }) {
    commit('LOADING_START', null, {root: true});
    try {
      const updatedQuote = {
        ...quote,
        ...{selected: false}
      };
      const response = await OrderService.updateQuote(updatedQuote);
      commit('updateQuote', {quote: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Updating quote failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async deleteQuoteWithConfirmation({commit}: any, {quote}: { quote: Quote }) {
    commit('SHOW_CONFIRMATION', {
      message: `Delete quote by ${quote.forwarderCompanyName}?`,
      action: 'orders/deleteQuote',
      payload: {quote}
    }, {root: true});
  },
  async deleteQuote({commit, dispatch}: any, {quote}: { quote: Quote }) {
    commit('LOADING_START', null, {root: true});
    try {
      if (!quote.uuid) return dispatch('SET_ALERT_MESSAGE', `Can't delete a quote without a uuid`, {root: true});
      await OrderService.deleteQuote(quote.uuid);
      commit('deleteQuote', {quote});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Deleting quote failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async fetchComments({commit}: any, {orderUuid}: { orderUuid: string }) {
    commit('LOADING_START', null, {root: true});
    try {
      const comments: OrderComment[] = await OrderService.fetchComments(orderUuid);
      commit('LOADING_FINISH', null, {root: true});
      commit('setComments', {orderUuid, comments});
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  async createComment({commit, dispatch}: any, {orderUuid, comment}: { orderUuid: string, comment: string }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await OrderService.createComment(orderUuid, comment);
      commit('createComment', {comment: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Creating comment failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async updateComment({commit, dispatch}: any, {comment}: { comment: OrderComment }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await OrderService.updateComment(comment);
      commit('updateComment', {comment: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Updating comment failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async deleteCommentWithConfirmation({commit}: any, {comment}: { comment: OrderComment }) {
    commit('SHOW_CONFIRMATION', {
      message: `Delete comment?`,
      action: 'orders/deleteComment',
      payload: {comment}
    }, {root: true});
  },
  async deleteComment({commit, dispatch}: any, {comment}: { comment: OrderComment }) {
    commit('LOADING_START', null, {root: true});
    try {
      if (!comment.uuid) return dispatch('SET_ALERT_MESSAGE', `Can't delete a comment without a uuid`, {root: true});
      await OrderService.deleteComment(comment.uuid);
      commit('deleteComment', {comment});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Deleting comment failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async fetchDocuments({commit}: any, {orderUuid}: { orderUuid: string }) {
    commit('LOADING_START', null, {root: true});
    try {
      const documents: OrderDocument[] = await OrderService.fetchDocuments(orderUuid);
      commit('LOADING_FINISH', null, {root: true});
      commit('setDocuments', {orderUuid, documents});
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  async createDocument({commit, dispatch}: any, {orderUuid, document}: { orderUuid: string, document: OrderDocument }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await OrderService.createDocument(orderUuid, document);
      commit('createDocument', {document: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Creating document failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async updateDocument({commit, dispatch}: any, {document}: { document: OrderDocument }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await OrderService.updateDocument(document);
      commit('updateDocument', {document: response});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Updating document failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async deleteDocumentWithConfirmation({commit}: any, {document}: { document: OrderDocument }) {
    commit('SHOW_CONFIRMATION', {
      message: `Delete document?`,
      action: 'orders/deleteDocument',
      payload: {document}
    }, {root: true});
  },
  async deleteDocument({commit, dispatch}: any, {document}: { document: OrderDocument }) {
    commit('LOADING_START', null, {root: true});
    try {
      if (!document.uuid) return dispatch('SET_ALERT_MESSAGE', `Can't delete a document without a uuid`, {root: true});
      await OrderService.deleteDocument(document.uuid);
      commit('deleteDocument', {document});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Deleting document failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async rankUp({commit, dispatch}: any, {orderUuid}: { orderUuid: string }) {
    commit('LOADING_START', null, {root: true});
    try {
      const orders = await OrderService.rankUp(orderUuid);
      commit('setOrders', orders);
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Updating order rank failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async rankDown({commit, dispatch}: any, {orderUuid}: { orderUuid: string }) {
    commit('LOADING_START', null, {root: true});
    try {
      const orders = await OrderService.rankDown(orderUuid);
      commit('setOrders', orders);
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Updating order rank failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  }
};
